import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit3, Save, X, ExternalLink, FileText, Eye, EyeOff } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';

/**
 * Custom Pages CRUD — free-form pages served at /sayfa/{slug}.
 * Admin can create, edit, publish/unpublish, delete.
 */
export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null); // page object or 'new'
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setPages(await api.adminListPages()); } catch { toast.error('Sayfalar yüklenemedi'); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ id: null, slug: '', title: '', content_html: '', published: true, meta_title: '', meta_description: '' });
  const startEdit = (p) => setEditing({ ...p });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.title.trim()) { toast.error('Başlık zorunlu'); return; }
    setSaving(true);
    try {
      const payload = {
        slug: (editing.slug || editing.title).trim(),
        title: editing.title.trim(),
        content_html: editing.content_html || '',
        published: !!editing.published,
        meta_title: editing.meta_title || null,
        meta_description: editing.meta_description || null,
      };
      if (editing.id) {
        await api.adminUpdatePage(editing.id, payload);
        toast.success('Sayfa güncellendi');
      } else {
        await api.adminCreatePage(payload);
        toast.success('Sayfa oluşturuldu');
      }
      await load();
      setEditing(null);
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`"${p.title}" sayfası silinsin mi?`)) return;
    try { await api.adminDeletePage(p.id); await load(); toast.success('Silindi'); }
    catch { toast.error('Silinemedi'); }
  };

  if (editing) return (
    <div className="p-4 md:p-8 max-w-4xl" data-testid="admin-page-editor">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Sayfa</div>
          <h1 className="font-display text-2xl md:text-3xl text-ink-600 mt-1">{editing.id ? 'Sayfayı Düzenle' : 'Yeni Sayfa'}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={cancel} className="border border-ink-200 text-ink-500 rounded-full px-5 h-11 text-sm font-semibold inline-flex items-center gap-2 hover:bg-cream-200">
            <X className="w-4 h-4" /> Vazgeç
          </button>
          <button onClick={save} disabled={saving} data-testid="save-page" className="bg-ink-600 text-cream rounded-full px-6 h-11 text-sm font-semibold inline-flex items-center gap-2 hover:bg-ink-500 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-ink-100 p-6 md:p-8 space-y-5">
        <div>
          <label className="block text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">Başlık</label>
          <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="page-title" placeholder="Örn: Sıkça Sorulan Sorular" className="w-full h-11 px-4 rounded-lg border border-ink-100 text-ink-600 focus:outline-none focus:border-gold" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">URL Slug <span className="text-ink-400 font-normal normal-case">(boş bırakırsan başlıktan üretilir)</span></label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-400 tabular">/sayfa/</span>
            <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="page-slug" placeholder="sikca-sorulan-sorular" className="flex-1 h-11 px-4 rounded-lg border border-ink-100 text-ink-600 focus:outline-none focus:border-gold" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">İçerik</label>
          <RichTextEditor value={editing.content_html} onChange={(html) => setEditing({ ...editing, content_html: html })} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">SEO Başlık (isteğe bağlı)</label>
            <input value={editing.meta_title || ''} onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} placeholder="Google için başlık" className="w-full h-11 px-4 rounded-lg border border-ink-100 text-ink-500 focus:outline-none focus:border-gold" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">SEO Açıklama</label>
            <input value={editing.meta_description || ''} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} placeholder="Sayfa özeti" className="w-full h-11 px-4 rounded-lg border border-ink-100 text-ink-500 focus:outline-none focus:border-gold" />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-ink-600">
          <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="page-published" />
          Yayında (aksi halde sadece admin görebilir)
        </label>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-8" data-testid="admin-pages-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Sayfalar</div>
          <h1 className="font-display text-2xl md:text-3xl text-ink-600 mt-1">Özel Sayfalar</h1>
          <p className="text-ink-400 text-sm mt-1">Kendi oluşturduğun sayfalar. Menüye eklemek için Menü Yönetimi&apos;nden &quot;Sayfa seç&quot; ile bağla.</p>
        </div>
        <button onClick={startNew} data-testid="new-page" className="bg-ink-600 text-cream rounded-full px-5 h-11 text-sm font-semibold inline-flex items-center gap-2 hover:bg-ink-500">
          <Plus className="w-4 h-4" /> Yeni Sayfa
        </button>
      </div>

      {pages.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-ink-200 p-10 text-center">
          <FileText className="w-8 h-8 text-ink-300 mx-auto" />
          <div className="text-ink-500 text-sm mt-3">Henüz özel sayfa yok. İlk sayfanı oluştur ve menüye ekle.</div>
          <button onClick={startNew} className="mt-4 bg-ink-600 text-cream rounded-full px-5 h-11 text-sm font-semibold inline-flex items-center gap-2 hover:bg-ink-500">
            <Plus className="w-4 h-4" /> Yeni Sayfa Oluştur
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {pages.map(p => (
            <div key={p.id} className="bg-white rounded-2xl border border-ink-100 p-4 flex items-center gap-4 flex-wrap md:flex-nowrap" data-testid={`page-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg text-ink-600 truncate">{p.title}</div>
                <div className="text-xs text-ink-400 mt-0.5 truncate">/sayfa/{p.slug}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${p.published ? 'bg-green-50 text-green-700' : 'bg-ink-50 text-ink-400'}`}>
                {p.published ? <><Eye className="w-3 h-3" /> Yayında</> : <><EyeOff className="w-3 h-3" /> Taslak</>}
              </span>
              <div className="flex items-center gap-1">
                <a href={`/sayfa/${p.slug}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 grid place-items-center rounded-lg border border-ink-100 text-ink-400 hover:text-gold" title="Aç"><ExternalLink className="w-4 h-4" /></a>
                <button onClick={() => startEdit(p)} className="w-9 h-9 grid place-items-center rounded-lg border border-ink-100 text-ink-500 hover:text-ink-600 hover:border-ink-300" title="Düzenle"><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => del(p)} className="w-9 h-9 grid place-items-center rounded-lg border border-red-100 text-red-400 hover:bg-red-50" title="Sil"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
